import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';

import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../../components/common/forms/TextInput/TextInputComponent';
import validationSchema from '../validationSchema';
import AutocompleteComponent from '../../../../components/common/forms/Autcomplete/AutocompleteComponent';
import { API_URL } from '../../../../utils/constants';
import PrimaryButton from '../../../../components/common/forms/Button/PrimaryButton';
import TransportPricingsApi from '../../../../services/api/TransportPricingsApi';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import BorderlessButton from '~/src/components/common/forms/Button/BorderlessButton';
import styled from 'styled-components';

export default function TransportPricingEdit(): JSX.Element {
  const { id } = useParams();
  const [itemData, setItemData] = useState({
    countryId: '',
    inStandard: '',
    outStandard: '',
    inReefer: '',
    outReefer: '',
    dataArrived: false,
  });
  const history = useHistory();

  const saveItem = async (values, setSubmitting) => {
    setSubmitting(true);
    let res = null;
    if (id) {
      res = await TransportPricingsApi.update(id, values);
    } else {
      res = await TransportPricingsApi.create(values);
    }
    if (res.status === StatusCodes.OK) {
      history.push(`/transport-pricings`);
    }
    setSubmitting(false);
  };

  const getItem = async () => {
    const res = await TransportPricingsApi.get(id);
    const newItemData = { ...res.data, dataArrived: true };
    setItemData(newItemData);
  };

  useEffect(() => {
    if (id) {
      getItem(id);
    }
  }, []);

  const handleBack = () => {
    history.push('/transport-pricings');
  };

  const onDelete = async () => {
    const res = await TransportPricingsApi.delete(id);
    if (res.status === StatusCodes.OK) {
      history.push('/transport-pricings');
    }
  };

  return (
    <>
      <WhitePad>
        <TitleComponent title={`${id ? 'Edit ' : 'Create '} Transport Pricing`} />
        {id && itemData.dataArrived === false ? null : (
          <Formik
            initialValues={{
              countryId: itemData.countryId,
              inStandard: itemData.inStandard,
              outStandard: itemData.outStandard,
              inReefer: itemData.inReefer || '',
              outReefer: itemData.outReefer || '',
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              saveItem(values, setSubmitting);
            }}
          >
            {({ values, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, errors }) => (
              <form onSubmit={handleSubmit} autoComplete="off">
                <FormRow>
                  <AutocompleteComponent
                    url={`${API_URL}/countries/get`}
                    name="countryId"
                    label="Country"
                    initialValue={values.countryId}
                    onChange={setFieldValue}
                    getOptionLabel={(option) => `${option.name}`}
                    queryFields={['name']}
                    errorMessage={errors.countryId}
                  />
                </FormRow>
                <FormRow>
                  <TextInputComponent
                    label="inStandard"
                    name="inStandard"
                    value={values.inStandard}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="number"
                    cols={3}
                  />
                  <TextInputComponent
                    name="outStandard"
                    label="outStandard"
                    value={values.outStandard}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    type="number"
                  />
                  <TextInputComponent
                    name="inReefer"
                    label="inReefer"
                    value={values.inReefer}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    type="number"
                  />
                  <TextInputComponent
                    name="outReefer"
                    label="outReefer"
                    value={values.outReefer}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    cols={3}
                    type="number"
                  />
                </FormRow>
                <ActionWrapper>
                  <PrimaryButton isSubmitting={isSubmitting} type="submit" title={`${id ? 'Save ' : 'Create '}`} />
                  <BorderlessButton marginLeft="30px" size="sm" type="submit" onClick={onDelete} title="Delete" />
                </ActionWrapper>
              </form>
            )}
          </Formik>
        )}
      </WhitePad>
    </>
  );
}

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
`;
